import React, { useCallback, useMemo, useState } from 'react';
import { BsChevronDown, BsPencilSquare, BsPersonDash, BsPlusCircle } from 'react-icons/bs';
import { FaRegUserCircle } from 'react-icons/fa';
import { ImCheckboxChecked, ImCheckboxUnchecked } from 'react-icons/im';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Container, DrawerCloseButton, GridItem, HStack, Icon, Stack, Text } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import { FeatureFlags } from '../../../../../../../../../../../components/Authorizer';
import useCompanyFeatures from '../../../../../../../../../../../components/Authorizer/useCompanyFeatures';
import CertificateCreationFlow from '../../../../../../../../../../../components/CertificateCreationFlow';
import EditPolicyModal from '../../../../../../../../../../../components/CertificateCreationFlow/components/EditPolicyModal';
import IconButtonMenu from '../../../../../../../../../../../components/IconButtonMenu';
import StackedButtonGroup from '../../../../../../../../../../../components/Modal/ModalButtonGroups/StackedButtonGroup';
import { analyticsIds } from '../../../../../../../../../../../constants/analyticsIds';
import CompanySettingTypes from '../../../../../../../../../../../constants/companySettings';
import { CypressIds } from '../../../../../../../../../../../constants/cypressIds';
import { FeatureTypes } from '../../../../../../../../../../../constants/featureTypes';
import { inAppText } from '../../../../../../../../../../../constants/inAppText';
import {
  selectCompany,
  selectCompanyFeatures,
  selectCompanySettingValue
} from '../../../../../../../../../../../redux/modules/company';
import { emailEditorReset, updateInsuredRecipients } from '../../../../../../../../../../../redux/modules/emails';
import { resetAgentEmailModal } from '../../../../../../../../../../../redux/modules/insured';
import { hideModal, showModal, updateModalOptions } from '../../../../../../../../../../../redux/modules/modal';
import { hideTray, showTray } from '../../../../../../../../../../../redux/modules/tray';
import { selectUser } from '../../../../../../../../../../../redux/modules/user';
import { insuredCertificateCreator } from '../../../../../../../../../../../redux/reducer';
import { useAllCompanyPolicyTypesData } from '../../../../../../../../../../../services/company-service';
import { useInsuredData } from '../../../../../../../../../../../services/insured-service';
import { useUpdatePolicy } from '../../../../../../../../../../../services/policy-service';
import {
  useRecordData,
  useRecordDocumentRequirements,
  useRecordItemRequirements,
  useRecordLanguageRequirements,
  useRecordPolicyRequirements,
  useRemoveRecordAgent
} from '../../../../../../../../../../../services/record-service';
import EmailAgentModal from '../modals/EmailAgentModal';
import RemoveAgentModal from '../modals/RemoveAgentModal';

const { removeAgentModal: removeAgentModalText } = inAppText.insuredProfile.policiesTable;

const PolicyNameGrid = ({ policy, insuredId, companyId, recordId, jobId }) => {
  const dispatch = useDispatch();

  const { data: insured } = useInsuredData(insuredId);
  const { data: policyRequirements } = useRecordPolicyRequirements(recordId);
  const { data: itemRequirements } = useRecordItemRequirements(recordId);
  const { data: documentRequirements } = useRecordDocumentRequirements(recordId);
  const { data: languageRequirements } = useRecordLanguageRequirements(recordId);
  const { data: companyPolicyTypes } = useAllCompanyPolicyTypesData(companyId);
  const { data: record, isLoading: recordIsLoading } = useRecordData(recordId);

  const { mutate: updatePolicy } = useUpdatePolicy({ policyId: policy.id, policyTypeId: policy.insurancePolicyTypeId, recordId, insuredId });
  const { mutateAsync: removeAgent } = useRemoveRecordAgent({ recordId, insuredId });

  const company = useSelector(selectCompany);
  const user = useSelector(selectUser);
  const hideNonPolicyHolders = useSelector(selectCompanySettingValue(CompanySettingTypes.ReportsHideNonPolicyHolders));
  const { companyHasFeature: ocrEnabled } = useCompanyFeatures(FeatureFlags.OCR);
  const [isSubmitting, setSubmitting] = useState(false);
  const companyFeatures = useSelector(selectCompanyFeatures);

  const aiScanEnabled = useMemo(() => companyFeatures.find(f => [
    FeatureTypes.AiTierOne,
    FeatureTypes.AiTierTwo,
    FeatureTypes.AiTierThree
  ].includes(f.name))?.isEnabled, [companyFeatures]);

  const job = useMemo(() => {
    if (recordIsLoading) {
      return null;
    }

    return record?.job;
  }, [record]);

  const { parentId } = insured ?? {};
  const { isInheritable } = policy;

  const isInherited = isInheritable && policy.insureds?.length && !policy.insureds.find(p => p.insuredId === insuredId);

  const policyOwnerBelongsToCompany = useMemo(() => {
    if (policy.owner === null) return false;

    const ownerRoles = policy.owner?.roles;

    if (ownerRoles?.length) {
      return ownerRoles.some(role => (role.pivot.scopeType === 'company' && role.pivot.scopeId === parseInt(companyId, 10)));
    }

    return (policy.owner?.userLevel) && (policy.owner?.userLevel !== 3);
  }, [policy]);

  const emailAgentToolTip = useMemo(() => {
    if (!policy?.id) {
      return 'No policy provided';
    }

    if (policyOwnerBelongsToCompany) {
      return 'The policy submitter belongs to your company.';
    }

    return null;
  }, [policyOwnerBelongsToCompany, policy]);

  const editPolicyTooltip = useMemo(() => {
    if (!policy?.id) {
      return 'No policy provided';
    }

    if (!policyOwnerBelongsToCompany) {
      return 'This policy cannot be edited.  It was submitted by someone outside of your company.';
    }

    return null;
  }, [policyOwnerBelongsToCompany, policy]);

  const removeAgentTooltip = useMemo(() => {
    if (!policy?.id) {
      return 'No policy provided';
    }

    if (policyOwnerBelongsToCompany) {
      return 'The policy submitter belongs to your company.';
    }

    if (!policy.owner) {
      return 'No agent to remove.';
    }

    return null;
  }, [policyOwnerBelongsToCompany, policy]);

  const menuOptions = [
    {
      label: 'Email Agent',
      icon: FaRegUserCircle,
      id: 'emailAgent',
      dataAnalyticsId: analyticsIds.insuredProfilePage.policiesComponent.openEmailAgentModalButton,
      isDisabled: policyOwnerBelongsToCompany || !policy?.id,
      tooltip: emailAgentToolTip,
      handler: () => showAgentModal()
    },
    {
      label: 'Edit Policy',
      icon: BsPencilSquare,
      id: 'editPolicy',
      dataAnalyticsId: analyticsIds.insuredProfilePage.policiesComponent.openEditPolicyTrayButton,
      cypressId: CypressIds.policyOptionsUpdatePolicyButton(policy.insurancePolicyType.id, policy.id),
      isDisabled: !policy?.id,
      tooltip: !policy?.id ? 'No policy provided' : null,
      handler: () => {
        dispatch(showModal(() => (
          <EditPolicyModal
            blueprint={insuredCertificateCreator}
            cancelFunction={() => dispatch(hideModal())}
            company={company}
            policy={policy}
            policyType={policy.insurancePolicyType}
            showWarningMessage={false} // TODO: You are
            submitFunction={data => {
              updatePolicy(data);

              dispatch(hideModal());
            }}
          />
        ), { size: '3xl' }));
      }
    },
    {
      label: 'Add Policy',
      icon: BsPlusCircle,
      id: 'addPolicy',
      dataAnalyticsId: analyticsIds.insuredProfilePage.policiesComponent.openAddPolicyTrayButton,
      handler: () => {
        dispatch(showTray(() => (
          <>
            <DrawerCloseButton />
            <CertificateCreationFlow
              blueprint={insuredCertificateCreator}
              company={company}
              documentRequirements={documentRequirements}
              insuranceRequirement={record}
              insured={insured}
              insuredRequirements={policyRequirements}
              itemRequirements={itemRequirements}
              job={job}
              languageRequirements={languageRequirements}
              options={{
                ocr: true,
                shouldSync: true,
                aiScan: aiScanEnabled
              }}
              policyReviewContentHeight="75vh"
              policyTypes={companyPolicyTypes}
              user={user}
            />
          </>

        ), {
          size: '4xl',
          variant: 'secondary',
          onClose: () => dispatch(hideTray())
        }));
      }
    }
  ];

  const showAgentModal = useCallback(() => dispatch(showModal(EmailAgentModal, {
    size: '5xl',
    onClose: () => {
      dispatch(resetAgentEmailModal());
      dispatch(emailEditorReset());
      dispatch(updateInsuredRecipients(insuredId, []));
      dispatch(hideModal());
    }
  }, { companyId, policy, insuredId, recordId, jobId })));

  const showRemoveAgentModal = useCallback(() => dispatch(showModal(
    () => <RemoveAgentModal agentUserId={policy?.owner?.userId} recordId={recordId} />,
    {
      title: removeAgentModalText.title,
      size: 'md',
      buttons: {
        component: StackedButtonGroup,
        primaryButtonOptions: {
          label: removeAgentModalText.confirmButtonLabel,
          secondaryLabel: removeAgentModalText.cancelButtonLabel,
          primaryButtonVariant: 'gradientError',
          isDisabled: true,
          dataAnalyticsId: analyticsIds.insuredProfilePage.policiesComponent.removeAgentModal.confirmButton,
          onClick: async () => {
            dispatch(updateModalOptions({ buttons: { primaryButtonOptions: { isLoading: true } } }));

            try {
              await removeAgent(policy.owner.userId);
            } catch {
              // The mutation's onError toast reports the failure. Close on failure too:
              // the modal body latches into a spinner once a removal starts, so leaving
              // it open would strand the user on a permanent loading state.
            }

            dispatch(hideModal());
          }
        }
      }
    }
  )), [dispatch, policy, recordId, removeAgent]);

  const removeAgentOption = {
    label: 'Remove Agent',
    icon: BsPersonDash,
    id: 'removeAgent',
    dataAnalyticsId: analyticsIds.insuredProfilePage.policiesComponent.openRemoveAgentModalButton,
    cypressId: CypressIds.policyOptionsRemoveAgentButton(policy.insurancePolicyType.id, policy.id),
    isDisabled: !policy?.id || policyOwnerBelongsToCompany || !policy.owner,
    tooltip: removeAgentTooltip,
    handler: () => showRemoveAgentModal()
  };

  const getCheckBox = useCallback(() => {
    let text;
    let checkboxChecked = true;

    if (!policy.insureds?.length) {
      return null;
    }

    if (hideNonPolicyHolders && isInheritable && !isInherited && !parentId) {
      text = 'Applies to Sub Insureds';
    } else if (isInheritable && isInherited) {
      text = 'Inherited from Parent';
    } else if (!isInheritable && hideNonPolicyHolders) {
      text = 'Applies to Sub Insureds';
      checkboxChecked = false;
    }

    return (
      <Box p={2}>
        <HStack m={0} p={0}>
          <Icon
            aria-label={`checkbox is ${checkboxChecked ? '' : 'not'} checked`}
            as={checkboxChecked ? ImCheckboxChecked : ImCheckboxUnchecked}
            size="sm"
            visibility={text ? 'visible' : 'hidden'}
          />
          <Container
            fontSize={11}
            fontStyle="italic"
            ml={0}
            pl={0}
            textAlign="left"
            whiteSpace="nowrap"
          >
            {text}
          </Container>
        </HStack>
      </Box>
    );
  }, [isInheritable, isInherited, parentId, policy]);

  return (
    <GridItem colSpan={1} rowSpan={1}>
      <Stack direction="column" spacing={5}>
        <Box ml={0} px={2} textAlign="left">
          <HStack justifyContent="space-between" ml={0} pl={0}>
            <Text mr="15px" textStyle="bodyLg">{policy.insurancePolicyType.displayName}</Text>
            <Box>
              <IconButtonMenu
                ariaLabel="Agent Information Popup"
                cypressId={CypressIds.policyOptionsIconButton(policy.insurancePolicyType.id, policy.id)}
                height="40px"
                iconElement={<Icon aria-label="Open Policy Options Menu" as={BsChevronDown} size="md" />}
                isLoading={isSubmitting}
                menuListWidth="160px"
                menuOptions={[...menuOptions, removeAgentOption]}
              />
            </Box>
          </HStack>
        </Box>
        {getCheckBox()}
      </Stack>
    </GridItem>
  );
};

PolicyNameGrid.propTypes = {
  policy: PropTypes.shape({
    id: PropTypes.number,
    insurancePolicyType: PropTypes.shape({ id: PropTypes.number, displayName: PropTypes.string }),
    insureds: PropTypes.array,
    isInheritable: PropTypes.bool,
    owner: PropTypes.shape({ userId: PropTypes.number, roles: PropTypes.array, userLevel: PropTypes.number })
  }),
  companyId: PropTypes.number.isRequired,
  recordId: PropTypes.number.isRequired,
  insuredId: PropTypes.number.isRequired,
  jobId: PropTypes.number.isRequired
};

export default PolicyNameGrid;
